import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PageHeader} from "../components/layout/PageHeader";
import {Button, Col, Form, Input, InputNumber, Popconfirm, Row, Spin} from "antd";
import {useHistory} from "react-router-dom";
import {BillingMultiplierDto} from "../generated-api";
import {PlusCircle, Trash2, XCircle} from "react-feather";
import {
    deleteBillingSetting,
    getBillingSetting,
    updateBillingSetting,
    useBillingSetting,
    useBillingSettingDelete
} from "../hooks/billing";
import {validateMoreThan} from "../utils/validator";
import {useForm} from "antd/es/form/Form";
import {BackButton} from "../components/layout/BackButton";

export function BillingSettingPage({match}: any) {
    const {billingSettingId} = (match || {}).params || {};
    const {t} = useTranslation();
    const history = useHistory();
    const billingSettingResponse = useBillingSetting();
    const billingSettingDeleteResponse = useBillingSettingDelete();
    useEffect(() => {
        if (billingSettingId) {
            getBillingSetting(billingSettingId, true).then().catch(error => {
                console.error(error);
                history.push('/billingSettings');
            });
        } else {
            history.push('/billingSettings');
        }
    }, [billingSettingId]);
    const [newMultiplier, setNewMultiplier] = useState<BillingMultiplierDto | any>({});
    const [counter, setCounter] = useState(0);
    const billingSetting = billingSettingResponse.data;
    const {country} = billingSetting || {};
    const billingMultipliers = ((billingSetting || {}).billingMultipliers || []);
    const [form] = useForm();

    const onFinish = (values: any): void => {
        values.multipliers = values.billingMultipliers.map((item: BillingMultiplierDto) => {
            return {
                id: item.id,
                fromCertificates: item.fromCertificates,
                toCertificates: item.toCertificates,
                multiplierPercent: item.multiplierPercent
            };
        });
        delete values.billingMultipliers;
        updateBillingSetting(billingSettingId, values).then(() => {
            history.push('/billingSettings');
        }).catch(console.error);
    };

    const validateMessages = {
        required: t('error.required'),
        types: {
            number: t('error.type.number')
        }
    };

    const onDelete = (): void => {
        deleteBillingSetting(billingSettingId).then(() => {
            history.push('/billingSettings');
        }).catch(console.error);
    };

    const addMultiplier = (): void => {
        if (billingSetting) {
            billingSetting.billingMultipliers.push(newMultiplier);
            form.setFieldsValue(billingSetting);
        }
        setNewMultiplier({});
    };

    const removeMultiplier = (index: number): void => {
        if (billingSetting && !!billingSetting.billingMultipliers) {
            billingSetting.billingMultipliers.splice(index, 1);
        }
        setCounter(counter + 1);
    };

    return (
        <Spin spinning={billingSettingResponse.loading || billingSettingDeleteResponse.loading}>
            <PageHeader/>
            <div className={'top-block accent7-background'}>
                <div className={'flex-row'}>
                    <h1 className={'h1 flex-auto'}><BackButton backPath={'/billingSettings'}/>{t('billingSetting.title')}</h1>
                    <Popconfirm placement="bottomRight"
                                className={'title-icon-button ant-typography ant-typography-danger cursor-pointer'}
                                title={t('billingSetting.deleteConfirmation')}
                                onConfirm={onDelete}
                                okButtonProps={{danger: true}}
                                okText={t('general.yes')}
                                cancelText={t('general.no')}>
                        <Trash2/>
                    </Popconfirm>
                </div>
            </div>
            <div className={'subpage-content'}>
                <Form name="billingSetting" key={billingSettingResponse.loading}
                      onFinish={onFinish}
                      form={form}
                      autoComplete={'off'}
                      labelCol={{span: 11}}
                      validateMessages={validateMessages}
                      initialValues={billingSetting}
                      validateTrigger={'onSubmit'}>
                    <Row className={'row-offset'}>
                        <Col span={24} md={18} lg={12} xl={8}>
                            <Form.Item label={t('billingSetting.country')} className={'label-left'}>
                                <Input value={(country || {}).name} readOnly/>
                            </Form.Item>
                        </Col>
                        <Col span={24} md={18} lg={12} xl={8}>
                            <Form.Item label={t('billingSetting.price')} className={'label-left'}
                                       name={'price'} rules={[{required: true}]}>
                                <InputNumber/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <h2>{t('billingSetting.multipliers')}</h2>
                    <div className={'flex-row light-gray-block'}>
                        <Row className={'flex-auto'}>
                            <Col span={24} md={18} lg={12} xl={8}>
                                <Form.Item label={t('billingSetting.fromCertificates')}>
                                    <InputNumber value={newMultiplier.fromCertificates}
                                                 onChange={(value: any) => setNewMultiplier({
                                                     ...newMultiplier,
                                                     fromCertificates: value
                                                 })}/>
                                </Form.Item>
                            </Col>
                            <Col span={24} md={18} lg={12} xl={8}>
                                <Form.Item label={t('billingSetting.toCertificates')}>
                                    <InputNumber value={newMultiplier.toCertificates}
                                                 onChange={(value: any) => setNewMultiplier({
                                                     ...newMultiplier,
                                                     toCertificates: value
                                                 })}/>
                                </Form.Item>
                            </Col>
                            <Col span={24} md={18} lg={12} xl={8}>
                                <Form.Item label={t('billingSetting.multiplierPercent')}>
                                    <InputNumber value={newMultiplier.multiplierPercent}
                                                 onChange={(value: any) => setNewMultiplier({
                                                     ...newMultiplier,
                                                     multiplierPercent: value
                                                 })}/>
                                </Form.Item>
                            </Col>
                        </Row>
                        <Button type={'link'} className={'form-item-icon-button button-layout-bottom'}
                                onClick={addMultiplier}>
                            <PlusCircle/>
                        </Button>
                    </div>
                    <div key={counter}>
                        {billingMultipliers.map((item: BillingMultiplierDto, index: number) =>
                            <div key={index}
                                 className={'flex-row ' + ((index + 1) < billingMultipliers.length ? 'block-separated' : '')}>
                                <Row className={'flex-auto'}>
                                    <Col span={24} md={18} lg={12} xl={8}>
                                        <Form.Item label={t('billingSetting.fromCertificates')}
                                                   name={['billingMultipliers', index, 'fromCertificates']}
                                                   rules={[{required: true}]}>
                                            <InputNumber/>
                                        </Form.Item>
                                    </Col>
                                    <Col span={24} md={18} lg={12} xl={8}>
                                        <Form.Item label={t('billingSetting.toCertificates')}
                                                   name={['billingMultipliers', index, 'toCertificates']}
                                                   rules={[
                                                       {required: true},
                                                       () => ({
                                                           validator: (_, value) => {
                                                               return validateMoreThan(value, form.getFieldValue(['billingMultipliers', index, 'fromCertificates']), t);
                                                           }
                                                       })
                                                   ]}>
                                            <InputNumber/>
                                        </Form.Item>
                                    </Col>
                                    <Col span={24} md={18} lg={12} xl={8}>
                                        <Form.Item label={t('billingSetting.multiplierPercent')}
                                                   name={['billingMultipliers', index, 'multiplierPercent']}
                                                   rules={[{required: true}]}>
                                            <InputNumber/>
                                        </Form.Item>
                                    </Col>
                                </Row>
                                <Button type={'link'} className={'form-item-icon-button button-layout-bottom'}
                                        onClick={() => removeMultiplier(0)}>
                                    <XCircle className={'ant-typography ant-typography-danger'}/>
                                </Button>
                            </div>
                        )}
                    </div>
                    <Form.Item className={'no-margin text-right'}>
                        <Button type="primary" className={'accent7-background text-white w-100'} htmlType="submit">
                            {t('billingSetting.save')}
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </Spin>
    )
}
